// UserSelector.jsx
import React, { useState, useEffect, useContext } from 'react';
import { Form, Row, Col, Spinner, Alert, Pagination, InputGroup, Button } from 'react-bootstrap';
import { FaUser, FaUsers, FaSearch, FaChevronDown, FaChevronRight } from 'react-icons/fa';

import './UserSelector.css';
import { AuthContext } from '../../../contexts/AuthContext';

const UserSelector = ({ 
  onSelectUser, 
  onSelectDependent, 
  selectedUserId, 
  selectedDependentId 
}) => {
  const { token } = useContext(AuthContext);
  
  // States
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedUsers, setExpandedUsers] = useState({});
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  
  // Selected states
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedDependent, setSelectedDependent] = useState(null);
  
  // API Base URL
  const API_BASE_URL = 'https://aceswafund.org/welfare/welfare-system';

  useEffect(() => {
    fetchUsers();
  }, [token]);

  useEffect(() => {
    // If we have selectedUserId from props, find and expand that user
    if (selectedUserId && users.length > 0) {
      const user = users.find(u => u.user_id === selectedUserId);
      if (user) {
        setSelectedUser(user);
        setExpandedUsers(prev => ({ ...prev, [user.user_id]: true }));
      }
    }
  }, [selectedUserId, users]);

  useEffect(() => {
    // If we have selectedDependentId from props, find it
    if (selectedDependentId && selectedUser) {
      const dependent = selectedUser.dependents?.find(d => d.id === selectedDependentId);
      if (dependent) {
        setSelectedDependent(dependent);
      }
    }
  }, [selectedDependentId, selectedUser]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/admin-get-users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status && data.users) {
        setUsers(data.users);
        setTotalUsers(data.users.length);
      } else {
        setError(data.message || 'Failed to fetch users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message || 'Error fetching users');
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search
  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.full_name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.phone?.toLowerCase().includes(searchLower) ||
      user.dependents?.some(d => 
        d.name?.toLowerCase().includes(searchLower)
      )
    );
  });

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const toggleUserExpand = (userId) => {
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const handleUserClick = (user) => {
    const newSelectedUser = selectedUser?.user_id === user.user_id ? null : user;
    setSelectedUser(newSelectedUser);
    setSelectedDependent(null);
    onSelectUser(newSelectedUser);
    onSelectDependent(null);
    
    // Auto-expand the selected user
    if (newSelectedUser) {
      setExpandedUsers(prev => ({ ...prev, [user.user_id]: true }));
    }
  };

  const handleDependentClick = (dependent, user) => {
    // First ensure the parent user is selected
    if (selectedUser?.user_id !== user.user_id) {
      setSelectedUser(user);
      onSelectUser(user);
    }
    
    // Toggle dependent selection
    const newSelectedDependent = selectedDependent?.id === dependent.id ? null : dependent;
    setSelectedDependent(newSelectedDependent);
    onSelectDependent(newSelectedDependent);
  };

  const renderPagination = () => {
    let items = [];
    for (let number = 1; number <= totalPages; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }
    return items;
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="success" />
        <p className="mt-2 text-muted">Loading users...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="mb-0">
        <Alert.Heading>Error Loading Users</Alert.Heading>
        <p>{error}</p>
        <Button variant="outline-danger" size="sm" onClick={fetchUsers}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <div className="user-selector">
      {/* Search Bar */}
      <div className="mb-3">
        <InputGroup>
          <InputGroup.Text className="bg-light border-end-0">
            <FaSearch className="text-muted" />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search by name, email, phone, or dependent..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="border-start-0"
          />
          {searchTerm && (
            <Button 
              variant="outline-secondary" 
              onClick={() => setSearchTerm('')}
              size="sm"
            >
              Clear
            </Button>
          )}
        </InputGroup>
      </div>

      {/* Users List */}
      <div className="users-container border rounded">
        {currentUsers.length > 0 ? (
          <>
            {currentUsers.map((user) => (
              <div key={user.user_id} className="user-item border-bottom">
                {/* User Header */}
                <div 
                  className={`user-header p-3 d-flex align-items-center justify-content-between ${
                    selectedUser?.user_id === user.user_id ? 'bg-success bg-opacity-10' : ''
                  }`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleUserClick(user)}
                >
                  <div className="d-flex align-items-center flex-grow-1">
                    <div className="me-3">
                      {expandedUsers[user.user_id] ? (
                        <FaChevronDown className="text-success" />
                      ) : (
                        <FaChevronRight className="text-muted" />
                      )}
                    </div>
                    <div className="user-avatar me-3">
                      {user.profile_image ? (
                        <img 
                          src={user.profile_image} 
                          alt={user.full_name}
                          className="rounded-circle"
                          width="40"
                          height="40"
                          style={{ objectFit: 'cover' }}
                        />
                      ) : (
                        <div className="bg-success bg-opacity-10 p-2 rounded-circle">
                          <FaUser className="text-success" />
                        </div>
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center">
                        <h6 className="mb-0 me-2">{user.full_name}</h6>
                        {user.role && (
                          <span className={`badge bg-${user.role === 'admin' ? 'warning' : 'info'} bg-opacity-10 text-dark px-2 py-1`}>
                            {user.role}
                          </span>
                        )}
                      </div>
                      <small className="text-muted d-block">
                        {user.email} • {user.phone}
                      </small>
                    </div>
                  </div>
                  <div className="d-flex align-items-center">
                    <span className="badge bg-success me-2">
                      <FaUsers className="me-1" />
                      {user.dependents_count || 0} dependents
                    </span>
                    <Form.Check
                      type="radio"
                      name="userSelect"
                      checked={selectedUser?.user_id === user.user_id}
                      onChange={() => {}}
                      onClick={(e) => e.stopPropagation()}
                      className="ms-2"
                    />
                  </div>
                </div>

                {/* Dependents List */}
                {expandedUsers[user.user_id] && user.dependents && user.dependents.length > 0 && (
                  <div className="dependents-list bg-light p-3">
                    <h6 className="text-muted mb-3">Dependents:</h6>
                    <Row>
                      {user.dependents.map((dependent) => (
                        <Col md={6} key={dependent.id} className="mb-2">
                          <div 
                            className={`dependent-item p-2 rounded d-flex align-items-center justify-content-between ${
                              selectedDependent?.id === dependent.id ? 'bg-success bg-opacity-25 border border-success' : 'bg-white'
                            }`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleDependentClick(dependent, user)}
                          >
                            <div className="d-flex align-items-center">
                              <div className="dependent-avatar me-2">
                                <div className="bg-info bg-opacity-10 p-1 rounded-circle">
                                  <FaUser className="text-info" size={12} />
                                </div>
                              </div>
                              <div>
                                <span className="fw-semibold">{dependent.name}</span>
                                {dependent.relationship && (
                                  <span className="badge bg-secondary bg-opacity-10 text-dark ms-2">
                                    {dependent.relationship}
                                  </span>
                                )}
                                {dependent.date_of_birth_formatted && (
                                  <small className="text-muted d-block">
                                    DOB: {dependent.date_of_birth_formatted}
                                  </small>
                                )}
                              </div>
                            </div>
                            <Form.Check
                              type="radio"
                              name="dependentSelect"
                              checked={selectedDependent?.id === dependent.id}
                              onChange={() => {}}
                              onClick={(e) => e.stopPropagation()}
                              className="ms-2"
                            />
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}

                {/* No dependents message */}
                {expandedUsers[user.user_id] && (!user.dependents || user.dependents.length === 0) && (
                  <div className="bg-light p-3 text-center text-muted">
                    <small>No dependents added for this user</small>
                  </div>
                )}
              </div>
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-3 d-flex justify-content-center">
                <Pagination size="sm">
                  <Pagination.Prev
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  />
                  {renderPagination()}
                  <Pagination.Next
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-5">
            <FaUsers size={48} className="text-muted mb-3" />
            <h6>No users found</h6>
            <p className="text-muted small">Try adjusting your search terms</p>
          </div>
        )}
      </div>

      {/* Selection Summary */}
      {(selectedUser || selectedDependent) && (
        <div className="selection-summary mt-3 p-3 bg-success bg-opacity-10 rounded">
          <h6 className="text-success mb-2">Selected:</h6>
          {selectedUser && (
            <div className="d-flex align-items-center mb-2">
              <span className="badge bg-success me-2">User</span>
              <span>{selectedUser.full_name}</span>
            </div>
          )}
          {selectedDependent && (
            <div className="d-flex align-items-center">
              <span className="badge bg-info me-2">Dependent</span>
              <span>{selectedDependent.name}</span>
              {selectedDependent.relationship && (
                <span className="text-muted ms-2">({selectedDependent.relationship})</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserSelector;